/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnInit} from '@angular/core';
import * as _ from 'lodash';

import template from './url-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';

import {TARGETING_IMPORTS} from '../targeting-imports';
import {TargetingTemplateComponent} from './targeting-template.component';

@Component({
  imports: [...TARGETING_IMPORTS, TargetingTemplateComponent],
  selector: 'adn-url-targeting',
  template
})
export class UrlTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.url;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  inputted = {value: ''};

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();
      const model = this.model || {};
      return {
        vm: _.cloneDeep(this.inputted),
        model: _.cloneDeep(model),
        constant: ADN_TARGETING_TYPES.url,
        summary: (model.negated === true ? 'NOT: ' : '') + (model.urls || []).join(', ')
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  ngOnInit(): void {
    this.initFromModel();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.url.widget, prepareForSave, copyTemplate);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
  }

  private initFromModel(): void {
    const urls = _.get(this.model, 'urls') || [];
    const inputted = _.map(urls, (addy: string) => {
      return addy.indexOf(',') > -1 ? '"' + addy + '"' : addy;
    });
    this.inputted = {value: inputted.join(', ')};
    if (this.model && _.isNil(this.model.negated)) {
      this.model.negated = false;
    }
  }

  private prepareForSave(): void {
    if (!this.model) {
      return;
    }
    let inputtedValue = this.inputted.value || '';
    const doubleQuoteIndices = [];
    let index = inputtedValue.indexOf('"');
    while (index >= 0) {
      doubleQuoteIndices.push(index);
      index = inputtedValue.indexOf('"', index + 1);
    }
    if (doubleQuoteIndices.length % 2 === 0) {
      const portions = [];
      for (let i = 0; i < doubleQuoteIndices.length; i = i + 2) {
        portions.push(inputtedValue.substring(doubleQuoteIndices[i] + 1, doubleQuoteIndices[i + 1]));
      }
      _.forEach(portions, (portion) => {
        inputtedValue = inputtedValue.replace('"' + portion + '"', portion.replace(/,/g, 'THIS=IS=COMMA'));
      });
    }
    const values = inputtedValue.split(',');
    const trimmedValues = _.filter(_.map(values, (v) => {
      return _.trim(String(v).replace(/THIS=IS=COMMA/g, ','));
    }), (v) => {
      return _.isString(v) && v.length > 0 && v.indexOf(' ') < 0 && v.indexOf('"') < 0;
    });
    this.model.urls = trimmedValues;
    this.initFromModel();
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || {urls: []};
    Object.keys(this.model).forEach((key) => delete this.model[key]);
    Object.assign(this.model, copied);
    this.initFromModel();
  }
}
